import React, { useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useRecoverySummary, useWearableTimeseries } from '../../hooks/useWearables';
import {
  Card,
  Container,
  Grid,
  Group,
  Stack,
  Text,
  Title,
  Button,
  Badge,
  Skeleton,
  Select,
  TextInput,
  Alert,
} from '@mantine/core';
import { IconRefresh, IconHeart } from '@tabler/icons-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const RANGE_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom range' },
];

const RECOVERY_METRIC_OPTIONS = [
  { value: 'recovery_score', label: 'Recovery Score' },
  { value: 'resting_heart_rate', label: 'Resting Heart Rate' },
  { value: 'heart_rate_variability_rmssd', label: 'HRV (RMSSD)' },
];

const RECOVERY_METRIC_CONFIG = {
  recovery_score: {
    color: 'var(--mantine-color-yellow-6)',
    yLabel: 'Score',
    formatter: (value) => `${Math.round(value)}%`,
  },
  resting_heart_rate: {
    color: 'var(--mantine-color-red-6)',
    yLabel: 'BPM',
    formatter: (value) => `${Math.round(value)} bpm`,
  },
  heart_rate_variability_rmssd: {
    color: 'var(--mantine-color-blue-6)',
    yLabel: 'ms',
    formatter: (value) => `${Math.round(value)} ms`,
  },
};

function getDateRange(days) {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (days === 'all' ? 12000 : Number(days)));
  return { start_date: start.toISOString().split('T')[0], end_date: end.toISOString().split('T')[0] };
}

function toTimeRange(dateRange) {
  return {
    start_time: `${dateRange.start_date}T00:00:00Z`,
    end_time: `${dateRange.end_date}T23:59:59Z`,
  };
}

function Recovery() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [rangeDays, setRangeDays] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [cursor, setCursor] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('recovery_score');

  const selectedRange = rangeDays === 'custom'
    ? (
      customStart
        ? { start_date: customStart, end_date: customEnd || new Date().toISOString().split('T')[0] }
        : getDateRange(7)
    )
    : getDateRange(rangeDays);

  const { summaries, pagination, loading, error, refresh } = useRecoverySummary({
    limit: 50,
    startDate: selectedRange.start_date,
    endDate: selectedRange.end_date,
    cursor,
  });
  const { data: recoveryTimeseries, loading: recoveryTimeseriesLoading, refresh: refreshRecoveryTimeseries } = useWearableTimeseries({
    autoFetch: true,
    types: ['recovery_score', 'resting_heart_rate', 'heart_rate_variability_rmssd'],
    limit: 100,
    resolution: 'raw',
    ...toTimeRange(selectedRange),
  });

  const recoveryScoreValues = useMemo(
    () => (recoveryTimeseries || []).filter((x) => x.type === 'recovery_score').map((x) => x.value),
    [recoveryTimeseries]
  );
  const hrvValues = useMemo(
    () => (recoveryTimeseries || []).filter((x) => x.type === 'heart_rate_variability_rmssd').map((x) => x.value),
    [recoveryTimeseries]
  );
  const restingHrValues = useMemo(
    () => (recoveryTimeseries || []).filter((x) => x.type === 'resting_heart_rate').map((x) => x.value),
    [recoveryTimeseries]
  );

  const summaryAvgScore = summaries.length > 0
    ? summaries.reduce((s, x) => s + (x.recovery_score ?? 0), 0) / summaries.filter((x) => x.recovery_score != null).length
    : null;
  const summaryAvgHrv = summaries.filter((x) => x.avg_hrv_rmssd_ms != null).length > 0
    ? summaries.reduce((s, x) => s + (x.avg_hrv_rmssd_ms || 0), 0) / summaries.filter((x) => x.avg_hrv_rmssd_ms != null).length
    : null;
  const summaryAvgRestingHr = summaries.filter((x) => x.resting_heart_rate_bpm != null).length > 0
    ? Math.round(summaries.reduce((s, x) => s + (x.resting_heart_rate_bpm || 0), 0) / summaries.filter((x) => x.resting_heart_rate_bpm != null).length)
    : null;

  const avgScore = summaryAvgScore ?? (recoveryScoreValues.length ? (recoveryScoreValues.reduce((a, b) => a + b, 0) / recoveryScoreValues.length) : null);
  const avgHrv = summaryAvgHrv ?? (hrvValues.length ? (hrvValues.reduce((a, b) => a + b, 0) / hrvValues.length) : null);
  const avgRestingHr = summaryAvgRestingHr ?? (restingHrValues.length ? Math.round(restingHrValues.reduce((a, b) => a + b, 0) / restingHrValues.length) : null);
  const selectedMetricConfig = RECOVERY_METRIC_CONFIG[selectedMetric];
  const selectedMetricPoints = useMemo(
    () => (recoveryTimeseries || [])
      .filter((x) => x.type === selectedMetric)
      .map((x) => ({
        timestamp: new Date(x.timestamp).getTime(),
        value: x.value,
      }))
      .sort((a, b) => a.timestamp - b.timestamp),
    [recoveryTimeseries, selectedMetric]
  );

  const handleRangeChange = (value) => {
    const nextRange = value || '7';
    setCursor(null);
    setRangeDays(nextRange);
    if (nextRange !== 'custom') {
      refresh({ ...getDateRange(nextRange), limit: 50, cursor: null });
      refreshRecoveryTimeseries({ ...toTimeRange(getDateRange(nextRange)), types: ['recovery_score', 'resting_heart_rate', 'heart_rate_variability_rmssd'], limit: 100, resolution: 'raw' });
    }
  };

  if (authLoading) return (<Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400}><Skeleton height={400} /></Container>);
  if (!isAuthenticated) return (<Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400}><Card withBorder><Title order={2}>Recovery</Title><Text c="dimmed" mt="xs">Please log in.</Text></Card></Container>);

  return (
    <Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400} style={{ marginTop: 0 }}>
      <Stack gap="md">
        <Title order={1} size="h2" style={{ marginTop: 0 }}>Recovery</Title>
        {error && <Alert color="red" variant="light">{error}</Alert>}

        <Card withBorder radius="md" p="md">
          <Group justify="space-between" mb="md">
            <Text fw={600}>Recovery Scores</Text>
            <Group>
              <Select data={RANGE_OPTIONS} value={rangeDays} onChange={handleRangeChange} w={140} />
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={() => {
                  setCursor(null);
                  refresh({ ...selectedRange, limit: 50, cursor: null });
                  refreshRecoveryTimeseries({ ...toTimeRange(selectedRange), types: ['recovery_score', 'resting_heart_rate', 'heart_rate_variability_rmssd'], limit: 100, resolution: 'raw' });
                }}
                disabled={loading}
              >
                Refresh
              </Button>
            </Group>
          </Group>
          {rangeDays === 'custom' && (
            <Group mb="md">
              <TextInput
                label="Start date"
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
              />
              <TextInput
                label="End date"
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
              />
              <Button
                mt={24}
                variant="light"
                size="sm"
                onClick={() => {
                  setCursor(null);
                  refresh({ ...selectedRange, limit: 50, cursor: null });
                  refreshRecoveryTimeseries({ ...toTimeRange(selectedRange), types: ['recovery_score', 'resting_heart_rate', 'heart_rate_variability_rmssd'], limit: 100, resolution: 'raw' });
                }}
                disabled={!customStart || loading}
              >
                Load Data
              </Button>
            </Group>
          )}

          <Grid mb="md">
            <Grid.Col span={4}><Text size="sm" c="dimmed">Avg Recovery Score</Text><Text size="lg" fw={700}>{avgScore != null ? `${Math.round(avgScore)}%` : '-'}</Text></Grid.Col>
            <Grid.Col span={4}><Text size="sm" c="dimmed">Avg HRV (RMSSD)</Text><Text size="lg" fw={700}>{avgHrv != null ? `${Math.round(avgHrv)} ms` : '-'}</Text></Grid.Col>
            <Grid.Col span={4}><Text size="sm" c="dimmed">Avg Resting HR</Text><Text size="lg" fw={700}>{avgRestingHr != null ? `${avgRestingHr} bpm` : '-'}</Text></Grid.Col>
          </Grid>
          {recoveryTimeseriesLoading && (
            <Text size="xs" c="dimmed" mb="sm">Loading recovery metrics...</Text>
          )}

          <Card withBorder radius="md" p="md" mb="md">
            <Group justify="space-between" mb="md">
              <Text fw={600}>Recovery Trend</Text>
              <Select
                data={RECOVERY_METRIC_OPTIONS}
                value={selectedMetric}
                onChange={(val) => setSelectedMetric(val || 'recovery_score')}
                w={220}
              />
            </Group>
            {selectedMetricPoints.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={selectedMetricPoints}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="timestamp"
                    type="number"
                    scale="time"
                    domain={['dataMin', 'dataMax']}
                    tickFormatter={(v) => new Date(v).toLocaleDateString()}
                  />
                  <YAxis />
                  <Tooltip
                    labelFormatter={(v) => new Date(v).toLocaleString()}
                    formatter={(v) => [selectedMetricConfig.formatter(v), selectedMetricConfig.yLabel]}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={selectedMetricConfig.color}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Text size="sm" c="dimmed">No {RECOVERY_METRIC_OPTIONS.find((m) => m.value === selectedMetric)?.label || 'metric'} data in this range.</Text>
            )}
          </Card>

          {loading ? (
            <Stack gap="xs">{[1,2,3,4,5].map((i) => <Skeleton key={i} height={100} />)}</Stack>
          ) : summaries.length > 0 ? (
            <Stack gap="md">
              {summaries.map((s, idx) => {
                const date = s.date || (s.start_date || s.end_date);
                const score = s.recovery_score;
                const color = score >= 67 ? 'green' : score >= 34 ? 'yellow' : 'red';
                return (
                  <Card key={idx} withBorder radius="md" p="md">
                    <Group justify="space-between">
                      <div>
                        <Text fw={600}>{date}</Text>
                        <Text size="xs" c="dimmed">Recovery Score</Text>
                      </div>
                      <Badge size="lg" color={color} variant="light">{score != null ? `${score}%` : '-'}</Badge>
                      {s.resting_heart_rate_bpm != null && (
                        <Group gap="xs"><IconHeart size={16} /><Text size="sm">{s.resting_heart_rate_bpm} bpm</Text></Group>
                      )}
                    </Group>
                    {(s.avg_hrv_rmssd_ms != null || s.sleep_efficiency_percent != null) && (
                      <Text size="xs" c="dimmed" mt="xs">
                        {s.avg_hrv_rmssd_ms != null && `HRV: ${s.avg_hrv_rmssd_ms} ms`}
                        {s.sleep_efficiency_percent != null && ` • Sleep efficiency: ${Math.round(s.sleep_efficiency_percent)}%`}
                      </Text>
                    )}
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <Text c="dimmed" ta="center" py="xl">No recovery data available</Text>
          )}
          {(pagination?.previous_cursor || pagination?.has_more) && (
            <Group justify="space-between" mt="md">
              <Button
                variant="light"
                disabled={!pagination?.previous_cursor || loading}
                onClick={() => {
                  setCursor(pagination.previous_cursor);
                  refresh({ ...selectedRange, limit: 50, cursor: pagination.previous_cursor });
                }}
              >
                Previous Page
              </Button>
              <Text size="sm" c="dimmed">
                {pagination?.total_count != null ? `${pagination.total_count} total` : 'Paginated results'}
              </Text>
              <Button
                variant="light"
                disabled={!pagination?.has_more || loading}
                onClick={() => {
                  setCursor(pagination.next_cursor);
                  refresh({ ...selectedRange, limit: 50, cursor: pagination.next_cursor });
                }}
              >
                Next Page
              </Button>
            </Group>
          )}
        </Card>
      </Stack>
    </Container>
  );
}

export default Recovery;
